import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useOrder } from '../context/OrderContext';
import { Header, DashboardButton, AppVersionFooter } from '../layout';
import { setDocumentTitle } from '../utils/titleUtils';
import { DraftOrdersList } from '../features';
import ClientSelectionModal from '../modals/ClientSelectionModal';
import Swal from 'sweetalert2';
import { api } from '../services/api';

// Icon library for dashboard menu items
const icons = {
  edit: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
  plus: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>,
  document: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
  chart: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
  money: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
  tag: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
  box: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>,
  users: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
  userGroup: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>,
  clipboard: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>,
  chartBar: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
  cog: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
  server: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" /></svg>,
  fileText: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
};

// Factory function to create menu items
const createMenuItem = (title, iconName, onClick, isAdmin = false) => ({
  title,
  icon: icons[iconName],
  onClick,
  isAdmin
});

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state, dispatch } = useOrder();
  const [showClientModal, setShowClientModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'order' or 'offerta'

  const isAdmin = user?.role === 'admin';
  const isAgent = user?.userType === 'agent'; // Check if user is an agent
  // A funzionario is an agent with no AreaManager (head of area / responsabile)
  const isFunzionario = user?.userType === 'agent' && !user?.agent_data?.AreaManager;
  const [assignedClients, setAssignedClients] = useState(user?.assigned_clients || []);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Get cart items and current customer from state
  const cartItems = state?.cartItems || [];
  const currentCustomer = state?.customer;

  useEffect(() => {
    setDocumentTitle('Dashboard');
  }, []);

  const refreshAssignedClients = async () => {
    if (!isAgent || !user?.agent_data?.Salesperson) return;
    setClientsLoading(true);
    try {
      const res = await api.getAgentClients(user.agent_data.Salesperson);
      if (res.status === 'success' && res.clients) {
        setAssignedClients(res.clients);
      }
    } catch (e) {
      console.error('Error refreshing clients:', e);
    } finally {
      setClientsLoading(false);
    }
  };

  // Auto-set customer for clients on component mount
  useEffect(() => {
    if (user?.userType === 'client' && user?.client_data) {
      dispatch({ type: 'SET_CUSTOMER', payload: user.client_data });
    }
  }, [user, dispatch]);

  // Handler to save current cart as draft
  const handleSaveDraft = async () => {
    try {
      // Prepare draft order data with customer information
      const customerId = currentCustomer?.CustSupp || user?.id;

      if (!customerId) {
        await Swal.fire({
          icon: 'error',
          title: 'Errore',
          text: 'Nessun cliente selezionato'
        });
        return false;
      }

      // Determine who is creating the draft (agent or client)
      const isAgentUser = user?.role === 'agent' || user?.role === 'head_agent';
      // For agents, user.id is the Salesperson code
      const createdById = isAgentUser ? user?.id : customerId;
      const createdByType = isAgentUser ? 'agent' : 'client';

      const draftOrderData = {
        customer_id: customerId,
        created_by_id: createdById,
        created_by_type: createdByType,
        customer_data: currentCustomer, // Save complete customer data
        order_data: state.replaces
          ? { ...state.orderDetails, originalOrderNumber: state.replaces.orderNumber }
          : state.orderDetails,
        cart_items: cartItems
      };

      // Create or update draft order via API
      const api = await import('../services/api').then(m => m.api);

      if (state.draftOrderId) {
        await api.updateDraftOrder(state.draftOrderId, draftOrderData);
      } else {
        const result = await api.createDraftOrder(draftOrderData);
        dispatch({ type: 'SET_DRAFT_ORDER_ID', payload: result.id });
      }

      // Clear the cart after saving
      dispatch({ type: 'RESET_CART' });

      await Swal.fire({
        icon: 'success',
        title: 'Bozza salvata',
        text: `Il carrello per ${currentCustomer?.CompanyName || currentCustomer?.CustSupp} è stato salvato come bozza`,
        timer: 2000,
        showConfirmButton: false
      });

      return true;
    } catch (error) {
      console.error('Error saving draft:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Impossibile salvare la bozza: ' + (error.message || 'Errore sconosciuto')
      });
      return false;
    }
  };

  // Check if cart has items and offer to save as draft
  const checkCartBeforeNewOrder = async (action) => {
    if (cartItems.length > 0) {
      const customerName = currentCustomer?.CompanyName || currentCustomer?.CustSupp || 'sconosciuto';

      const result = await Swal.fire({
        icon: 'warning',
        title: 'Carrello esistente',
        html: `Hai già prodotti nel carrello per il cliente <strong>${customerName}</strong>.<br><br>Vuoi salvare il carrello corrente come bozza?`,
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Salva come bozza',
        denyButtonText: 'Elimina carrello',
        cancelButtonText: 'Annulla',
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#d33',
        cancelButtonColor: '#6c757d'
      });

      if (result.isConfirmed) {
        // Save as draft
        const saved = await handleSaveDraft();
        if (!saved) return false;
      } else if (result.isDenied) {
        // Clear cart
        const confirmClear = await Swal.fire({
          icon: 'warning',
          title: 'Conferma eliminazione',
          text: 'Sei sicuro di voler eliminare tutti i prodotti dal carrello?',
          showCancelButton: true,
          confirmButtonText: 'Sì, elimina',
          cancelButtonText: 'Annulla',
          confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d'
        });

        if (confirmClear.isConfirmed) {
          dispatch({ type: 'RESET_CART' });
        } else {
          return false;
        }
      } else {
        // User cancelled
        return false;
      }
    }

    return true;
  };

  // Handler for starting a new order - navigates to product categories
  const handleNewOrder = async () => {
    // Check if cart has items before proceeding
    const canProceed = await checkCartBeforeNewOrder('order');
    if (!canProceed) return;

    if (isAgent) {
      // L'agente puo' sempre aprire la scelta cliente: anche senza clienti assegnati puo' creare un nuovo cliente
      setPendingAction('order');
      refreshAssignedClients();
      setShowClientModal(true);
    } else {
      // Client: proceed directly
      dispatch({ type: 'SET_ORDER_TYPE', payload: 'ordine' });
      navigate('/product-categories');
    }
  };

  // Handler for requesting an offerta
  const handleNewOfferta = async () => {
    // Check if cart has items before proceeding
    const canProceed = await checkCartBeforeNewOrder('offerta');
    if (!canProceed) return;

    if (isAgent) {
      // Per le offerte l'agente puo' sempre aprire la scelta cliente: anche senza clienti assegnati puo' creare un nuovo cliente
      setPendingAction('offerta');
      refreshAssignedClients();
      setShowClientModal(true);
    } else {
      // Client: proceed directly to product categories with offerta type
      dispatch({ type: 'SET_ORDER_TYPE', payload: 'offerta' });
      navigate('/product-categories', { state: { type: 'offerta' } });
    }
  };

  // Handler for when client is selected from modal
  const handleClientModalClose = () => {
    setShowClientModal(false);
    setPendingAction(null);
  };

  // Override SET_CUSTOMER to also navigate after selection
  const handleClientSelection = (client) => {
    dispatch({ type: 'SET_CUSTOMER', payload: client });
    setShowClientModal(false);

    // Set order type in context based on pending action
    if (pendingAction === 'order') {
      dispatch({ type: 'SET_ORDER_TYPE', payload: 'ordine' });
      navigate('/product-categories');
    } else if (pendingAction === 'offerta') {
      dispatch({ type: 'SET_ORDER_TYPE', payload: 'offerta' });
      navigate('/product-categories', { state: { type: 'offerta' } });
    }

    setPendingAction(null);
  };

  // Common user menu items
  const userMenuItems = [
    createMenuItem('Richiesta Offerta', 'edit', handleNewOfferta),
    createMenuItem('Nuovo Ordine', 'plus', handleNewOrder),
    createMenuItem('Consultazione Documenti', 'document', () => navigate('/documents')),
    createMenuItem('Stato Ordini Mago', 'chart', () => navigate('/orders-status')),
    createMenuItem('Ordini e Offerte da App', 'clipboard', () => navigate('/client-orders')),
    ...((isAgent || user?.role === 'agent' || user?.role === 'head_agent' || !!user?.agent_data) ? [createMenuItem('Gestione Clienti', 'users', () => navigate('/clients'))] : [])
  ];

  // Capitolato: visibile a admin e funzionari, nascosto agli agenti normali
  const canSeeCapitolato = isAdmin || isFunzionario;
  const capitolatoMenuItems = canSeeCapitolato ? [
    createMenuItem('Capitolato', 'fileText', () => navigate('/capitolato-categories')),
    createMenuItem('Storico Capitolati', 'clipboard', () => navigate('/storico-capitolati'))
  ] : [];

  // Admin menu items - Gestione section
  const adminGestioneItems = isAdmin ? [
    createMenuItem('Gestione Articoli', 'box', () => navigate('/articles'), true),
    createMenuItem('Gestione Clienti', 'users', () => navigate('/clients'), true),
    createMenuItem('Gestione Agenti', 'userGroup', () => navigate('/agents'), true),
    createMenuItem('Gestione Documenti', 'document', () => navigate('/admin/documents'), true),
    createMenuItem('Invii Certificazioni', 'fileText', () => navigate('/invii-certificazioni'), true)
  ] : [];

  // Admin menu items - Statistiche section
  const adminStatisticheItems = isAdmin ? [
    {
      title: 'Statistiche Generali',
      onClick: () => navigate('/admin/stats/general'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      isAdmin: true
    },
    {
      title: 'Statistiche Personalizzate',
      onClick: () => navigate('/admin/stats/custom'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      isAdmin: true
    }
  ] : [];

  // Admin menu items - Configurazione section
  const adminConfigItems = isAdmin ? [
    {
      title: 'Configurazione Generale',
      onClick: () => navigate('/admin/config/general'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      isAdmin: true
    },
    {
      title: 'Modalità Manutenzione',
      onClick: () => navigate('/maintenance'),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
      isAdmin: true
    }
  ] : [];

  // Combine all admin items
  const adminMenuItems = [...adminGestioneItems, ...adminStatisticheItems, ...adminConfigItems];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50">
      <div className="absolute inset-0 bg-[radial-gradient(#cd212a_0.5px,transparent_0.5px)] [background-size:24px_24px] opacity-20" />

      <Header showBack={false} />

      <div className="relative container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-light text-gray-800 mb-2">
            Benvenuto, <span className="font-medium" style={{ color: '#cd212a' }}>{user?.name}</span>
            {isAdmin && <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">Admin</span>}
          </h1>
          <p className="text-gray-600">Seleziona un'opzione per iniziare</p>
        </div>

        {/* User Section */}
        <div className="mb-8">
          <h2 className="text-xl font-medium text-gray-700 mb-4 border-b border-red-200 pb-2">
            Funzionalità Utente
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 animate-fade-in">
            {userMenuItems.map((item, index) => (
              <DashboardButton
                key={`user-${index}`}
                title={item.title}
                onClick={item.onClick}
                icon={item.icon}
              />
            ))}
          </div>

          {/* Draft Orders List */}
          <DraftOrdersList />
        </div>

        {/* Capitolato Section - admin e funzionari */}
        {canSeeCapitolato && capitolatoMenuItems.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-medium text-gray-700 mb-4 border-b border-red-200 pb-2">
              Generatore Capitolato
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 animate-fade-in">
              {capitolatoMenuItems.map((item, index) => (
                <DashboardButton
                  key={`capitolato-${index}`}
                  title={item.title}
                  onClick={item.onClick}
                  icon={item.icon}
                />
              ))}
            </div>
          </div>
        )}


        {/* Admin Section - Only shown if user is admin */}
        {isAdmin && adminMenuItems.length > 0 && (
          <div>
            <h2 className="text-xl font-medium text-gray-700 mb-4 border-b border-red-200 pb-2">
              Funzionalità Amministratore
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 animate-fade-in">
              {adminMenuItems.map((item, index) => (
                <DashboardButton
                  key={`admin-${index}`}
                  title={item.title}
                  onClick={item.onClick}
                  icon={item.icon}
                  isAdmin={item.isAdmin}
                />
              ))}
            </div>
          </div>
        )}

        <AppVersionFooter />
      </div>

      {/* Client Selection Modal for Agents */}
      <ClientSelectionModal
        isOpen={showClientModal}
        onClose={handleClientModalClose}
        onSelect={handleClientSelection}
        clients={assignedClients}
        allowNewClient
      />

      {/* Cart Button */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .animate-fade-in {
            animation: fadeIn 0.5s ease-out forwards;
          }

          @media (max-width: 768px) {
            .container {
              padding: 1rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Dashboard;
